import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MembershipRole } from '@/features/auth/api/auth';
import { apiRequest } from '@/lib/http';

export interface Member {
  id: string;
  userId: string;
  name: string;
  email: string;
  role: MembershipRole;
  canSeeFinancials: boolean;
  twoFactorEnabled: boolean;
  joinedAt: string;
}

export interface Invitation {
  id: string;
  email: string;
  role: MembershipRole;
  canSeeFinancials: boolean;
  expiresAt: string;
  createdAt: string;
}

export interface InvitationPreview {
  organizationName: string;
  email: string;
  role: MembershipRole;
  accountExists: boolean;
}

export const ROLE_LABELS: Record<MembershipRole, string> = {
  ADMIN: 'Administrador',
  MANAGER: 'Gestor',
  OPERATOR: 'Operador',
  VIEWER: 'Leitor',
};

export const organizationKeys = {
  all: ['organization'] as const,
  members: () => [...organizationKeys.all, 'members'] as const,
  invitations: () => [...organizationKeys.all, 'invitations'] as const,
};

export function useMembers() {
  return useQuery({
    queryKey: organizationKeys.members(),
    queryFn: () => apiRequest<Member[]>('/organization/members'),
  });
}

export function useInvitations() {
  return useQuery({
    queryKey: organizationKeys.invitations(),
    queryFn: () => apiRequest<Invitation[]>('/organization/invitations'),
  });
}

function useInvalidateOrganization() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: organizationKeys.all });
}

export function useInvite() {
  const invalidate = useInvalidateOrganization();
  return useMutation({
    mutationFn: (input: { email: string; role: MembershipRole; canSeeFinancials: boolean }) =>
      apiRequest<Invitation>('/organization/invitations', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useUpdateMember() {
  const invalidate = useInvalidateOrganization();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      role: MembershipRole;
      canSeeFinancials: boolean;
    }) => apiRequest<void>(`/organization/members/${id}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  });
}

export function useRemoveMember() {
  const invalidate = useInvalidateOrganization();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/organization/members/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useResendInvitation() {
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/organization/invitations/${id}/resend`, { method: 'POST' }),
  });
}

export function useRevokeInvitation() {
  const invalidate = useInvalidateOrganization();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/organization/invitations/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function getInvitationPreview(token: string): Promise<InvitationPreview> {
  // Token no corpo, nunca no endereço: endereço cai em log (ADR-056).
  return apiRequest('/invitations/preview', { method: 'POST', body: { token }, skipAuth: true });
}

export function acceptInvitation(
  token: string,
  body: { name?: string; password: string },
): Promise<void> {
  return apiRequest('/invitations/accept', {
    method: 'POST',
    body: { ...body, token },
    skipAuth: true,
  });
}
